import { Controller } from "@hotwired/stimulus"

const ROW_CONTROLS = "a, button, select, textarea, label, input:not([type=checkbox]), .dropdown, [data-bs-toggle]"

// Selection of a selectable turbo index (`_scroll_table` with the option `selectable:`). It lives in
// sessionStorage, keyed by the index, so it survives the reloads of a search, a filter or a page: each row
// brings its id, amount and flags, and the form sends the ids as `selected_ids[]`. Selecting the whole filter
// is remembered with the filter signature and dropped as soon as the filter changes; rows then unchecked are
// sent as `excluded_ids[]`. With `defaultAll`, the whole filter starts selected, and again on each new filter.
export default class extends Controller {
  static targets = ["checkbox", "pageToggle", "bar", "summary", "amount", "allMatching", "allMatchingPrompt", "count", "feedback", "selectedCount", "requiresSelection"]
  static values = {
    storageKey: String,
    signature: String,
    totalCount: Number,
    totalAmount: Number,
    currency: String,
    reset: Boolean,
    defaultAll: Boolean,
    feedbackTimeout: Number,
    oneTemplate: String,
    otherTemplate: String,
    allMatchingTemplate: String
  }

  connect() {
    this.format = this.currencyValue ? new Intl.NumberFormat(document.documentElement.lang || "fr-FR", { style: "currency", currency: this.currencyValue }) : null
    this.state = this.resetValue ? this.emptyState() : this.load()
    if (this.state.allMatching && this.state.allMatching !== this.signatureValue) this.state.allMatching = null
    if (this.defaultAllValue && this.state.filter !== this.signatureValue) this.selectWholeFilter()
    this.state.filter = this.signatureValue
    this.checkboxTargets.forEach((checkbox) => { checkbox.checked = this.selected(checkbox.dataset.id) })
    this.save()
    this.render()
    this.releaseFeedback()
    this.endDrag = () => { this.dragging = null }
    this.dragOnScroll = () => this.dragAtPointer()
    document.addEventListener("mouseup", this.endDrag)
    document.addEventListener("scroll", this.dragOnScroll, { capture: true, passive: true })
  }

  disconnect() {
    clearTimeout(this.feedbackTimeout)
    document.removeEventListener("mouseup", this.endDrag)
    document.removeEventListener("scroll", this.dragOnScroll, { capture: true })
  }

  // A row toggles when pressed anywhere but its links and menus; Shift extends from the last row pressed,
  // and dragging carries the new state over the rows passed.
  pressRow(event) {
    if (event.button !== 0 || event.target.closest(ROW_CONTROLS)) return

    const checkbox = this.rowCheckbox(event.target)
    if (!checkbox) return

    event.preventDefault()
    const checked = !checkbox.checked
    const rows = event.shiftKey && this.checkboxTargets.includes(this.anchor) ? this.rowsBetween(this.anchor, checkbox) : [checkbox]
    rows.forEach((box) => {
      box.checked = checked
      this.remember(box)
    })
    this.anchor = checkbox
    this.dragging = { checked, last: checkbox, point: [event.clientX, event.clientY] }
    this.selectionChanged()
  }

  dragRow(event) {
    if (!this.dragging) return
    // Button released outside the window: no mouseup came.
    if (!(event.buttons & 1)) return this.endDrag()

    this.dragging.point = [event.clientX, event.clientY]
    this.dragTo(this.rowCheckbox(event.target))
  }

  // Rows scrolled under a still pointer get no mouseover.
  dragAtPointer() {
    if (!this.dragging) return

    const target = document.elementFromPoint(...this.dragging.point)
    if (target && this.element.contains(target)) this.dragTo(this.rowCheckbox(target))
  }

  // Fast moves skip rows between two events: fill the gap from the last row reached.
  dragTo(checkbox) {
    if (!checkbox || checkbox === this.dragging.last) return

    const from = this.checkboxTargets.includes(this.dragging.last) ? this.dragging.last : checkbox
    const rows = this.rowsBetween(from, checkbox).filter((box) => box.checked !== this.dragging.checked)
    this.dragging.last = checkbox
    if (rows.length === 0) return

    rows.forEach((box) => {
      box.checked = this.dragging.checked
      this.remember(box)
    })
    this.selectionChanged()
  }

  // The press already toggled the box: its own click would toggle it back. The keyboard keeps its toggle.
  keepPressedBox(event) {
    if (event.detail > 0 && this.checkboxTargets.includes(event.target)) event.preventDefault()
  }

  rowCheckbox(target) {
    const row = target.closest("tr")
    return row && this.checkboxTargets.find((checkbox) => row.contains(checkbox))
  }

  rowsBetween(from, to) {
    const [start, end] = [this.checkboxTargets.indexOf(from), this.checkboxTargets.indexOf(to)].sort((a, b) => a - b)
    return this.checkboxTargets.slice(start, end + 1)
  }

  // A new selection makes way for its actions: the last feedback goes. The whole filter stays selected, less the
  // rows unchecked since.
  selectionChanged() {
    this.save()
    this.render()
    this.dismissFeedback({ fade: false })
  }

  // Turbo marks the form and the frame busy; the clicked button and a bulk change are shown here too.
  pending(event) {
    const { formElement, submitter } = event.detail.formSubmission
    if (submitter) {
      submitter.setAttribute("aria-busy", "true")
      submitter.disabled = true
    }
    if (formElement.method.toLowerCase() !== "get") this.element.classList.add("selectable-index--saving")
  }

  settled(event) {
    if (event.detail.success) return

    const { submitter } = event.detail.formSubmission
    if (submitter) {
      submitter.removeAttribute("aria-busy")
      submitter.disabled = false
    }
    this.element.classList.remove("selectable-index--saving")
  }

  holdFeedback() {
    clearTimeout(this.feedbackTimeout)
  }

  releaseFeedback() {
    if (!this.hasFeedbackTarget || !this.feedbackTimeoutValue) return

    clearTimeout(this.feedbackTimeout)
    this.feedbackTimeout = setTimeout(() => this.dismissFeedback(), this.feedbackTimeoutValue * 1000)
  }

  // Without the fade when a selection replaces it, or it would linger beside the actions that appear at once.
  dismissFeedback({ fade = true } = {}) {
    if (!this.hasFeedbackTarget) return

    clearTimeout(this.feedbackTimeout)
    const feedback = this.feedbackTarget
    if (!fade) return feedback.remove()

    feedback.classList.add("selectable-index__feedback--leaving")
    setTimeout(() => feedback.remove(), 200)
  }

  toggle(event) {
    this.remember(event.target)
    this.selectionChanged()
  }

  togglePage() {
    const checked = this.pageToggleTarget.checked
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = checked
      this.remember(checkbox)
    })
    this.selectionChanged()
  }

  selectAllMatching() {
    this.selectWholeFilter()
    this.save()
    this.render()
    this.dismissFeedback({ fade: false })
  }

  // Cleared for this filter: `defaultAll` selects again only on another one.
  clear() {
    this.state = { ...this.emptyState(), filter: this.signatureValue }
    this.checkboxTargets.forEach((checkbox) => { checkbox.checked = false })
    this.save()
    this.render()
  }

  appendSelection(event) {
    const [field, ids] = this.state.allMatching ? ["excluded_ids[]", this.state.excluded] : ["selected_ids[]", this.state.items]
    Object.keys(ids).forEach((id) => event.formData.append(field, id))
  }

  selectWholeFilter() {
    this.state.allMatching = this.signatureValue
    this.state.excluded = {}
  }

  selected(id) {
    return this.state.allMatching ? !(id in this.state.excluded) : id in this.state.items
  }

  // Within the whole filter, an unchecked row is an exception; otherwise a checked row is an item.
  remember(checkbox) {
    const id = checkbox.dataset.id
    const entry = { amount: Number(checkbox.dataset.amount || 0), flags: JSON.parse(checkbox.dataset.flags || "{}") }
    const [list, keep] = this.state.allMatching ? [this.state.excluded, !checkbox.checked] : [this.state.items, checkbox.checked]
    if (keep) list[id] = entry
    else delete list[id]
  }

  render() {
    const entries = Object.values(this.state.items)
    const excluded = Object.values(this.state.excluded)
    const allMatching = !!this.state.allMatching
    const count = allMatching ? Math.max(this.totalCountValue - excluded.length, 0) : entries.length
    const amount = allMatching ? this.totalAmountValue - this.sum(excluded) : this.sum(entries)
    const onPage = this.checkboxTargets.filter((checkbox) => checkbox.checked).length
    const pageFull = this.checkboxTargets.length > 0 && onPage === this.checkboxTargets.length

    if (this.hasAllMatchingTarget) this.allMatchingTarget.value = allMatching ? "1" : "0"
    if (this.hasPageToggleTarget) {
      this.pageToggleTarget.checked = pageFull
      this.pageToggleTarget.indeterminate = onPage > 0 && !pageFull
    }
    this.selectedCountTargets.forEach((target) => { target.textContent = count })
    if (this.format) this.amountTargets.forEach((target) => { target.textContent = this.format.format(amount) })
    this.requiresSelectionTargets.forEach((target) => { target.disabled = count === 0 })
    if (!this.hasBarTarget) return

    this.barTarget.classList.toggle("selectable-index__bar--empty", count === 0)
    this.allMatchingPromptTarget.classList.toggle("d-none", !pageFull || allMatching || this.totalCountValue <= this.checkboxTargets.length)
    this.summaryTarget.textContent = this.summary(count, allMatching && excluded.length === 0)
    this.countTargets.forEach((target) => {
      const flagged = entries.filter((entry) => entry.flags[target.dataset.flag]).length
      target.textContent = `(${flagged})`
      target.classList.toggle("d-none", allMatching)
    })
  }

  summary(count, allMatching) {
    const pattern = allMatching ? this.allMatchingTemplateValue : (count > 1 ? this.otherTemplateValue : this.oneTemplateValue)
    return pattern.replace("%{count}", count)
  }

  sum(entries) {
    return entries.reduce((total, entry) => total + entry.amount, 0)
  }

  emptyState() {
    return { items: {}, allMatching: null, excluded: {}, filter: null }
  }

  load() {
    try {
      return { ...this.emptyState(), ...JSON.parse(sessionStorage.getItem(this.storageKeyValue) || "{}") }
    } catch {
      return this.emptyState()
    }
  }

  save() {
    try {
      sessionStorage.setItem(this.storageKeyValue, JSON.stringify(this.state))
    } catch {
      // Storage off (private mode, quota): the selection then lasts until the next reload only.
    }
  }
}
